import { Input, Select, Table, Tag } from "antd";
import { useState } from "react";
import { useApplications, useAuditLog } from "../api/queries.js";
import type { AuditAction, AuditRecord } from "../api/types.js";
import { DiffView } from "../components/DiffView.js";
import { formatTimestamp } from "../format.js";

const PAGE_SIZE = 25;

const ACTION_COLORS: Record<AuditAction, string> = {
  create: "success",
  update: "warning",
  delete: "error",
  import: "processing",
};

export function AuditPage() {
  const [application, setApplication] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);

  const applications = useApplications();
  const audit = useAuditLog({ application, name, username, action, page, pageSize: PAGE_SIZE });

  return (
    <div>
      <div className="page-toolbar">
        <Select
          style={{ width: 220 }}
          placeholder="any application"
          allowClear
          showSearch
          value={application || undefined}
          onChange={(value) => {
            setApplication(value ?? "");
            setPage(1);
          }}
          options={(applications.data ?? []).map((info) => ({
            value: info.application,
            label: info.application,
          }))}
        />
        <Input.Search
          style={{ width: 200 }}
          placeholder="property name contains"
          allowClear
          onSearch={(value) => {
            setName(value);
            setPage(1);
          }}
        />
        <Input.Search
          style={{ width: 160 }}
          placeholder="user"
          allowClear
          onSearch={(value) => {
            setUsername(value);
            setPage(1);
          }}
        />
        <Select
          style={{ width: 140 }}
          placeholder="any action"
          allowClear
          value={action || undefined}
          onChange={(value) => {
            setAction(value ?? "");
            setPage(1);
          }}
          options={(["create", "update", "delete", "import"] as const).map((value) => ({
            value,
            label: value,
          }))}
        />
      </div>

      <Table
        size="middle"
        rowKey="id"
        loading={audit.isLoading}
        dataSource={audit.data?.records ?? []}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: audit.data?.total ?? 0,
          onChange: setPage,
          showSizeChanger: false,
          showTotal: (total) => `${total} records`,
        }}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (record: AuditRecord) => (
            <div style={{ padding: "4px 0" }}>
              {record.message && (
                <div style={{ marginBottom: 10 }}>
                  <span className="microlabel">note </span>
                  {record.message}
                </div>
              )}
              <DiffView oldValue={record.oldValue} newValue={record.newValue} />
            </div>
          ),
        }}
        columns={[
          {
            title: "when",
            dataIndex: "ts",
            width: 170,
            render: (ts: number) => (
              <span className="mono dim" style={{ whiteSpace: "nowrap" }}>
                {formatTimestamp(ts)}
              </span>
            ),
          },
          {
            title: "user",
            dataIndex: "username",
            width: 120,
          },
          {
            title: "action",
            dataIndex: "action",
            width: 100,
            render: (value: AuditAction) => (
              <Tag bordered={false} color={ACTION_COLORS[value]}>
                {value}
              </Tag>
            ),
          },
          {
            title: "application",
            dataIndex: "application",
            width: 140,
            render: (value: string) => <span className="mono">{value}</span>,
          },
          {
            title: "name",
            dataIndex: "name",
            render: (value: string) => <span className="mono" style={{ fontWeight: 500 }}>{value}</span>,
          },
          {
            title: "target",
            dataIndex: "target",
            render: (value: string) => <span className="target-chip">{value}</span>,
          },
          {
            title: "#",
            dataIndex: "updateId",
            width: 80,
            render: (value: number) => <span className="mono dim">#{value}</span>,
          },
        ]}
      />
    </div>
  );
}
